// ── LangContext.js — 직원별 언어 설정 (V99) ───────────────────────────────
//
// 언어는 "로그인한 사람 본인"의 설정이다. 매장(owner) 설정이 아니다.
// 그래서 users/{ownerUid}가 아니라 users/{본인 uid}.lang 에 저장한다.
// → 사장님은 한국어로, 허페이 직원은 중국어로 같은 매장 데이터를 본다.
//
// 품목명(name_zh)은 이 설정과 무관하게 항상 병기된다.
// 사장님과 직원이 같은 표를 보고 소통해야 하기 때문.

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../db';
import { tr } from './ui';
import { useStoreCountry, fmtMoney, moneyUnit } from '../storeLocale';
import { setTrLang, setTrCountry, zhLookup } from './L';   // V160 화면 전체 중국어

export const LANGS = [
  { id: 'ko', label: '한국어', native: '한국어' },
  { id: 'zh', label: '중국어', native: '中文' },
];

const LangContext = createContext({
  lang: 'ko',
  setLang: () => {},
  t: (ko) => ko,
  bi: (ko) => ko,
  ready: true,
  country: 'KR',
  fmt: (n) => `${Math.round(Number(n) || 0).toLocaleString('ko-KR')}원`,
  unit: '원',
});

const LS_KEY = 'semfit_lang';

// V144: 저장된 언어가 없을 때 — 휴대폰·브라우저 언어가 중국어면 zh
export function browserLang() {
  try {
    const list = (typeof navigator !== 'undefined' && (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language])) || [];
    return String(list[0] || '').toLowerCase().startsWith('zh') ? 'zh' : 'ko';
  } catch (e) { return 'ko'; }
}

// 로컬 우선 로드 — Firestore를 기다리는 동안 화면이 한국어로 깜빡이지 않게.
function readLocal() {
  try {
    const v = localStorage.getItem(LS_KEY);
    return v === 'zh' || v === 'ko' ? v : null;
  } catch { return null; }
}

export function LangProvider({ user, ownerUid, children }) {
  // V108: 매장 국가(통화)도 여기서 같이 제공한다. 언어는 '보는 사람'의 설정이고
  // 국가는 '매장'의 속성이라 축이 다르지만, 소비하는 쪽은 같은 훅이 편하다.
  const { country } = useStoreCountry(ownerUid);
  // V144: 처음 쓰는 기기는 휴대폰·브라우저 언어가 중국어면 中文으로 시작 (로그인 화면부터)
  const [lang, setLangState] = useState(() => readLocal() || browserLang());
  const [ready, setReady] = useState(false);

  // Firestore에 저장된 값으로 동기화 (다른 기기에서 바꿔도 따라옴)
  useEffect(() => {
    let cancelled = false;
    if (!user?.uid) { setReady(true); return; }

    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        const saved = snap.exists() ? snap.data()?.lang : null;
        if (!cancelled && (saved === 'ko' || saved === 'zh')) {
          setLangState(saved);
          try { localStorage.setItem(LS_KEY, saved); } catch {}
        }
      } catch (e) {
        // 오프라인이면 로컬 값으로 계속 간다
        console.debug('[lang] 설정 로드 실패:', e?.message);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => { cancelled = true; };
  }, [user?.uid]);

  const setLang = useCallback(async (next) => {
    if (next !== 'ko' && next !== 'zh') return;
    setLangState(next);
    try { localStorage.setItem(LS_KEY, next); } catch {}
    if (!user?.uid) return;
    try {
      await setDoc(doc(db, 'users', user.uid), { lang: next }, { merge: true });
    } catch (e) {
      console.debug('[lang] 설정 저장 실패:', e?.message);
    }
  }, [user?.uid]);

  // V160: 화면 전체 중국어 — L()이 쓰는 언어를 맞추고, 바뀌면 App에 알려 화면을 다시 그리게 한다
  setTrLang(lang);
  setTrCountry(country);
  useEffect(() => {
    try { window.dispatchEvent(new CustomEvent('semfit:lang', { detail: { lang, country } })); } catch (e) { /* 오래된 브라우저 */ }
  }, [lang, country]);

  // UI 고정 문구 (V160: 큰 사전 zhDict도 같이)
  const t = useCallback((ko) => (lang === 'zh' ? (zhLookup(ko) || tr(ko, lang)) : ko), [lang]);

  // 품목명 병기 — 언어 설정과 무관하게 항상 둘 다 보여준다.
  // 중국어가 아직 없으면 한국어만.
  const bi = useCallback((ko, zh) => {
    const k = String(ko || '').trim();
    const z = String(zh || '').trim();
    if (!z) return k;
    if (!k) return z;
    return `${k} / ${z}`;
  }, []);

  return (
    <LangContext.Provider value={{
      lang, setLang, t, bi, ready,
      country,
      fmt: (n) => fmtMoney(n, country),
      unit: moneyUnit(country),
    }}>
      {children}
    </LangContext.Provider>
  );
}

// ── 번역 문구 컴포넌트 ────────────────────────────────────────────────────
// App.js처럼 LangProvider를 "직접 들고 있는" 컴포넌트는 자기 안에서 useLang()을
// 불러도 Provider 바깥이라 기본값만 받는다. 그래서 Provider 안쪽에서 렌더되는
// 이 작은 컴포넌트로 감싼다.  <T k="단가표" />
export function T({ k }) {
  const { t } = useLang();
  return <>{t(k)}</>;
}

// V147: 한국어·중국어 문구(또는 JSX)를 따로 줄 때 — <LangText ko={<>…</>} zh={<>…</>} />
export function LangText({ ko, zh }) {
  const { lang } = useLang();
  return <>{lang === 'zh' && zh != null ? zh : ko}</>;
}

// V147: Provider 바깥(App의 로딩 화면·확인 창 등)에서 쓸 언어 — 이 기기에 저장된 값, 없으면 휴대폰 언어
export function storedLang() {
  return readLocal() || browserLang();
}

// ── 헤더용 한/中 토글 ─────────────────────────────────────────────────────
// 설정 탭은 상단 탭바가 가로로 밀리면 화면 밖으로 사라진다. 중국 직원이
// 태블릿을 처음 켰을 때 언어부터 못 바꾸면 아무것도 못 하므로,
// 항상 보이는 헤더에 토글을 둔다.
export function LangToggle({ compact = false }) {
  const { lang, setLang } = useLang();
  const btn = (id, label) => {
    const on = lang === id;
    return (
      <button key={id} onClick={() => setLang(id)}
        title={id === 'ko' ? '한국어로 보기' : '\u4e2d\u6587\u663e\u793a'}
        style={{
          padding: compact ? '4px 8px' : '5px 10px',
          border: 'none', background: 'none',
          color: on ? '#9A3412' : '#A1A1AA',
          fontSize: compact ? 11 : 12,
          fontWeight: on ? 800 : 600,
          cursor: 'pointer', lineHeight: 1.2,
        }}>{label}</button>
    );
  };
  return (
    <div style={{
      display: 'flex', alignItems: 'center', flexShrink: 0,
      border: '1px solid #F4F4F5', borderRadius: 10,
      background: '#fff', overflow: 'hidden',
    }}>
      {btn('ko', '\ud55c')}
      <span style={{ width: 1, alignSelf: 'stretch', background: '#F4F4F5' }} />
      {btn('zh', '\u4e2d')}
    </div>
  );
}

export function useLang() {
  return useContext(LangContext);
}

export default LangContext;
